/*!
 * Copyright © 2018-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { DOCUMENT } from '@angular/common';
import {
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  HostListener,
  Input,
  Renderer2,
  OnDestroy,
  Injector,
  ApplicationRef,
  ElementRef,
  TemplateRef,
  Inject
} from '@angular/core';

import { TooltipComponent } from './tooltip.component';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[fmTooltip]'
})
export class TooltipDirective implements OnDestroy {
  @Input()
  public fmTooltip: string;

  @Input()
  public fmTooltipTemplate: TemplateRef<any>;

  @Input()
  public fmTooltipXOffset = 0;

  @Input()
  public fmTooltipYOffset = 0;

  @Input()
  public shouldDisplayFmTooltip = true;

  @Input()
  public showTooltipIfOverflow = false;

  @Input()
  public fmTooltipShowOnTouch = false;

  private _componentRef: ComponentRef<TooltipComponent>;
  private _isTouch: boolean;
  private _touchTimer: any;

  constructor(
    private readonly _renderer: Renderer2,
    private readonly _resolver: ComponentFactoryResolver,
    private readonly _applicationRef: ApplicationRef,
    private readonly _injector: Injector,
    private readonly _elementRef: ElementRef,
    @Inject(DOCUMENT) private readonly _document: Document
  ) {}

  public ngOnDestroy(): void {
    this._destroy();
  }

  @HostListener('touchstart', ['$event'])
  public onTouchStart(event: TouchEvent): void {
    if (this.fmTooltipShowOnTouch) {
      clearTimeout(this._touchTimer);
      this._isTouch = true;

      if (this._componentRef) {
        this._destroy();
      } else {
        this._render(event.touches[0].pageX - this.fmTooltipXOffset, event.touches[0].pageY - this.fmTooltipYOffset);
      }
      this._touchTimer = setTimeout(() => {
        this._isTouch = false;
      }, 100);
      event.preventDefault();
    }
  }

  @HostListener('document:touchstart', ['$event.target'])
  onDocumentTouched(_target: HTMLElement): void {
    if (!this._isTouch && _target && !_target.classList.contains('tooltip-container')) {
      this._destroy();
    }
  }

  @HostListener('mousemove', ['$event'])
  public onMouseMove(event: MouseEvent): void {
    if ((this.showTooltipIfOverflow && !this._isEllipsisActive()) || this._isTouch || !this.shouldDisplayFmTooltip) {
      return;
    }

    if (this._componentRef) {
      this._componentRef.instance.setPosition(event.pageX - this.fmTooltipXOffset, event.pageY - this.fmTooltipYOffset);
    } else {
      this._render(event.pageX - this.fmTooltipXOffset, event.pageY - this.fmTooltipYOffset);
    }
  }

  @HostListener('mouseleave')
  public onMouseLeave(): void {
    this._destroy();
  }

  public rerender(event: MouseEvent): void {
    if (!this._componentRef) {
      return;
    }

    this._destroy();
    this._render(event.pageX, event.pageY);
  }

  public hide(): void {
    this._destroy();
  }

  private _destroy(): void {
    if (!this._componentRef) {
      return;
    }

    const componentElement = this._componentRef.location.nativeElement;
    componentElement.parentNode.removeChild(componentElement);
    this._applicationRef.detachView(this._componentRef.hostView);
    this._componentRef.destroy();
    this._componentRef = null;
  }

  private _render(pageX: number, pageY: number): void {
    const element = this._renderer.createText(this.fmTooltip);
    const factory = this._resolver.resolveComponentFactory(TooltipComponent);
    const injector = Injector.create({
      providers: [],
      parent: this._injector
    });
    this._componentRef = factory.create(injector, [[element]]);
    this._componentRef.instance.eventPageX = pageX;
    this._componentRef.instance.eventPageY = pageY;
    this._componentRef.instance.template = this.fmTooltipTemplate;
    this._applicationRef.attachView(this._componentRef.hostView);
    this._document.body.appendChild(this._componentRef.location.nativeElement);
  }

  private _isEllipsisActive(): boolean {
    return this._elementRef.nativeElement.offsetWidth < this._elementRef.nativeElement.scrollWidth;
  }
}
